import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark, faHandshake, faCalendarAlt, faClock, faEuroSign,
  faLocationDot, faCheckCircle, faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

const Section = ({ title, children }) => (
  <div style={{
    background: "#fff", borderRadius: "10px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden",
  }}>
    <div style={{
      padding: "10px 16px", background: "#f8f9fb",
      borderBottom: "1px solid #f0f0f0",
      fontSize: "0.7rem", fontWeight: 700,
      textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e",
    }}>
      {title}
    </div>
    <div style={{ padding: "14px 16px" }}>{children}</div>
  </div>
);

const StatCard = ({ icon, label, value, accent }) => (
  <div style={{
    background: "#fff", borderRadius: "10px", padding: "14px 16px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    display: "flex", alignItems: "center", gap: "12px",
  }}>
    <div style={{
      width: "36px", height: "36px", borderRadius: "9px",
      background: accent + "20", color: accent,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: "0.9rem", flexShrink: 0,
    }}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div>
      <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e" }}>
        {label}
      </div>
      <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "#212121" }}>{value || "—"}</div>
    </div>
  </div>
);

const SkillPill = ({ name, color = "#e65100", bg = "#fff3e0" }) => (
  <span style={{
    display: "inline-block", padding: "3px 12px",
    background: bg, color, borderRadius: "20px",
    fontSize: "0.8rem", fontWeight: 600, margin: "3px 4px 3px 0",
  }}>
    {name}
  </span>
);

const ListItem = ({ text }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: "8px",
    padding: "5px 0", borderBottom: "1px solid #f5f5f5",
    fontSize: "0.88rem", color: "#424242",
  }}>
    <span style={{ color: "#e65100", marginTop: "2px", fontSize: "0.7rem" }}>▶</span>
    {text}
  </div>
);

const JobView = ({ job, showView, setShowView }) => {
  if (!job) return null;

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <>
      <style>{`
        .job-view-modal {
          border-radius: 14px !important; overflow: hidden !important;
          border: none !important; box-shadow: 0 8px 40px rgba(0,0,0,0.18) !important;
        }
        .modal-90w { max-width: 90vw !important; width: 90vw !important; }
      `}</style>
      <Modal size="lg" show={showView} onHide={() => setShowView(false)}
        dialogClassName="modal-90w" contentClassName="job-view-modal">

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #e65100 0%, #bf360c 100%)",
          padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "8px",
              background: "rgba(255,255,255,0.2)", display: "flex",
              alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "1rem",
            }}>
              <FontAwesomeIcon icon={faHandshake} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#fff" }}>{job.jobtitle}</div>
              {job.start && (
                <div style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.75)", marginTop: "2px" }}>
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "5px" }} />
                  {fmt(job.start)}{job.end ? ` → ${fmt(job.end)}` : ""}
                </div>
              )}
            </div>
          </div>
          <button onClick={() => setShowView(false)} style={{
            background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "8px",
            width: "32px", height: "32px", display: "flex", alignItems: "center",
            justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1rem",
          }}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Body */}
        <div style={{ background: "#f8f9fb", padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>

          {/* Description */}
          {job.description && (
            <div style={{
              background: "#fff", borderRadius: "10px", padding: "14px 18px",
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)", fontSize: "0.92rem",
              color: "#616161", lineHeight: 1.7,
            }}>
              {job.description}
            </div>
          )}

          {/* Stat cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
            <StatCard icon={faHandshake} label="Contract" value={job.contract_type} accent="#e65100" />
            <StatCard
              icon={job.on_site ? faLocationDot : faTimesCircle}
              label="On Site"
              value={job.on_site ? "Yes" : "No"}
              accent={job.on_site ? "#2e7d32" : "#9e9e9e"}
            />
            <StatCard icon={faClock} label="Total Hours" value={job.max_hours ? `${job.max_hours} h` : null} accent="#1a73e8" />
            <StatCard icon={faEuroSign} label="Hourly Rate" value={job.max_hourly_rate ? `€ ${job.max_hourly_rate}` : null} accent="#7b1fa2" />
          </div>

          {/* Skills + Languages */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <Section title="Must-have Skills">
              {job.key_skills && job.key_skills.length > 0
                ? job.key_skills.map((s, i) => <SkillPill key={i} name={s.name} />)
                : <span style={{ color: "#bdbdbd", fontSize: "0.85rem" }}>None listed.</span>}
              {job.other_skills && job.other_skills.length > 0 && (
                <div style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #f0f0f0" }}>
                  <div style={{ fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#9e9e9e", marginBottom: "6px" }}>Nice to have</div>
                  {job.other_skills.map((s, i) => <SkillPill key={i} name={s.name} color="#1a73e8" bg="#e8f0fe" />)}
                </div>
              )}
            </Section>

            <Section title="Languages">
              {job.languages && job.languages.length > 0
                ? job.languages.map((lang, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "6px 0", borderBottom: "1px solid #f5f5f5", fontSize: "0.88rem",
                    }}>
                      <span style={{ color: "#424242", fontWeight: 500 }}>{lang.name}</span>
                      <span style={{
                        padding: "2px 10px", background: "#e8f0fe", color: "#1a73e8",
                        borderRadius: "20px", fontSize: "0.78rem", fontWeight: 700,
                      }}>{lang.level}%</span>
                    </div>
                  ))
                : <span style={{ color: "#bdbdbd", fontSize: "0.85rem" }}>None required.</span>}
            </Section>
          </div>

          {/* Tasks + We offer */}
          {((job.to_do && job.to_do.length > 0) || (job.we_offer && job.we_offer.length > 0)) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {job.to_do && job.to_do.length > 0 && (
                <Section title="Your Tasks">
                  {job.to_do.map((t, i) => <ListItem key={i} text={t.name} />)}
                </Section>
              )}
              {job.we_offer && job.we_offer.length > 0 && (
                <Section title="We Offer">
                  {job.we_offer.map((o, i) => (
                    <div key={i} style={{
                      display: "flex", alignItems: "center", gap: "8px",
                      padding: "5px 0", borderBottom: "1px solid #f5f5f5",
                      fontSize: "0.88rem", color: "#424242",
                    }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#2e7d32", fontSize: "0.75rem" }} />
                      {o.name}
                    </div>
                  ))}
                </Section>
              )}
            </div>
          )}

          {/* Publish period */}
          {(job.publish_from || job.publish_to) && (
            <div style={{
              background: "#fff", borderRadius: "10px", padding: "12px 16px",
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              display: "flex", alignItems: "center", gap: "8px",
              fontSize: "0.85rem", color: "#9e9e9e",
            }}>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>Published: <strong style={{ color: "#424242" }}>{fmt(job.publish_from)}</strong> → <strong style={{ color: "#424242" }}>{fmt(job.publish_to)}</strong></span>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{
          background: "#fff", borderTop: "1px solid #f0f0f0",
          padding: "14px 24px", display: "flex", justifyContent: "flex-end",
          borderRadius: "0 0 14px 14px",
        }}>
          <button onClick={() => setShowView(false)} style={{
            padding: "8px 18px", background: "#f5f5f5", color: "#424242",
            border: "none", borderRadius: "8px", fontWeight: 600,
            fontSize: "0.88rem", cursor: "pointer",
          }}>
            Close
          </button>
        </div>
      </Modal>
    </>
  );
};

export default JobView;
